import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import {
  dismissPushPrompt,
  setPushResumeAfterLogin,
  shouldOfferStandalonePushPrompt,
} from "../../lib/pushPromptStorage";

async function canShowPushPromptNow() {
  if (!shouldOfferStandalonePushPrompt()) return false;
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "denied") {
      dismissPushPrompt();
      return false;
    }
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) {
      dismissPushPrompt();
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

/**
 * One-time prompt on first open from the home-screen icon (any route under AppShell).
 * After login from "Anmelden & aktivieren", the prompt shows again for one tap to allow OS notifications.
 */
export default function PushStandalonePrompt() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    supported,
    permission,
    subscribed,
    loading,
    error,
    enablePush,
  } = usePushNotifications({ enabled: true });
  const [visible, setVisible] = useState(false);

  const evaluateVisibility = useCallback(async () => {
    if (!supported) {
      setVisible(false);
      return;
    }
    const show = await canShowPushPromptNow();
    setVisible(show);
  }, [supported]);

  useEffect(() => {
    evaluateVisibility();
  }, [evaluateVisibility, user]);

  useEffect(() => {
    if (subscribed || permission === "denied") {
      setVisible(false);
      if (subscribed || permission === "denied") {
        dismissPushPrompt();
      }
    }
  }, [subscribed, permission]);

  if (!visible) return null;

  const handleDismiss = () => {
    dismissPushPrompt();
    setVisible(false);
  };

  const handleEnable = async () => {
    if (!user) {
      setPushResumeAfterLogin();
      const redirect = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }
    try {
      await enablePush();
      dismissPushPrompt();
      setVisible(false);
    } catch {
      /* hook sets error */
    }
  };

  return (
    <div
      className="push-standalone-prompt"
      role="dialog"
      aria-labelledby="push-standalone-prompt-title"
      aria-describedby="push-standalone-prompt-desc"
    >
      <div className="push-standalone-prompt-inner">
        <h2 id="push-standalone-prompt-title" className="push-standalone-prompt-title">
          Erinnerungen aktivieren?
        </h2>
        <p id="push-standalone-prompt-desc" className="push-standalone-prompt-desc">
          {user
            ? "Albis kann dich rechtzeitig anstupsen — z. B. vor etwas im Plan. Einmal erlauben, dann passt das."
            : "Melde dich an — danach kannst du Erinnerungen mit einem Tipp aktivieren."}
        </p>
        {error ? <p className="push-standalone-prompt-error">{error}</p> : null}
        <div className="push-standalone-prompt-actions">
          <button
            type="button"
            className="push-standalone-prompt-btn push-standalone-prompt-btn-primary"
            onClick={handleEnable}
            disabled={loading}
          >
            {loading
              ? "Wird eingerichtet…"
              : user
                ? "Erinnerungen aktivieren"
                : "Anmelden & aktivieren"}
          </button>
          <button
            type="button"
            className="push-standalone-prompt-btn push-standalone-prompt-btn-secondary"
            onClick={handleDismiss}
            disabled={loading}
          >
            Später
          </button>
        </div>
      </div>
    </div>
  );
}
